import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/config';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Check, X, ExternalLink, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const VendorVerificationList = () => {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchPendingVerifications();
  }, []);

  const fetchPendingVerifications = async () => {
    try {
      const token = localStorage.getItem('token');
      // Fetch all vendors, then filter (or create a dedicated endpoint)
      // For MVP, we'll fetch all profiles via a new admin endpoint or filter existing
      // Let's assume we add an endpoint: /admin/verifications
      const response = await axios.get(`${API}/admin/verifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVendors(response.data);
    } catch (error) {
      console.error('Error fetching verifications:', error);
      toast.error('Failed to load verification requests');
    } finally {
      setLoading(false);
    }
  };

  const handleVerification = async (userId, action) => {
    const reason = action === 'reject' ? prompt('Reason for rejection:') : null;
    if (action === 'reject' && !reason) return;

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/admin/vendors/${userId}/verify`,
        null,
        {
          params: { action, reason },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      toast.success(`Vendor ${action}ed`);
      fetchPendingVerifications();
    } catch (error) {
      console.error('Error verifying vendor:', error);
      toast.error(`Failed to ${action} vendor`);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" onClick={() => navigate('/admin/dashboard')}>
          <ArrowLeft className="w-4 h-4 mr-2" /> Back
        </Button>
        <h1 className="text-2xl font-bold">Pending Verifications</h1>
      </div>

      {loading ? (
        <div>Loading...</div>
      ) : vendors.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-gray-500">
            No pending verification requests.
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {vendors.map(vendor => (
            <Card key={vendor.user_id}>
              <CardHeader>
                <CardTitle className="flex justify-between items-center">
                  {vendor.business_name || 'Unnamed Business'}
                  <Badge variant="outline">{vendor.contact_email}</Badge>
                </CardTitle>
                <CardDescription>
                  Status: <span className="font-semibold text-yellow-600">{vendor.verification_status}</span>
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-start gap-4">
                  {vendor.verification_document_url && (
                    <div className="flex-1">
                      <p className="text-sm font-medium mb-2">Submitted Document:</p>
                      <a 
                        href={vendor.verification_document_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-block relative group"
                      >
                        <img 
                          src={vendor.verification_document_url} 
                          alt="Verification Doc" 
                          className="h-48 object-contain border rounded bg-gray-50"
                        />
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white rounded">
                          <ExternalLink className="w-6 h-6" />
                        </div>
                      </a>
                    </div>
                  )}
                  <div className="flex flex-col gap-2">
                    <Button 
                      className="bg-green-600 hover:bg-green-700"
                      onClick={() => handleVerification(vendor.user_id, 'approve')}
                    >
                      <Check className="w-4 h-4 mr-2" /> Approve
                    </Button>
                    <Button 
                      variant="destructive"
                      onClick={() => handleVerification(vendor.user_id, 'reject')}
                    >
                      <X className="w-4 h-4 mr-2" /> Reject
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default VendorVerificationList;
